import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

export type ThemeVariant = 'classic' | 'pure-black' | 'pure-white' | 'trippy';

interface ThemeContextType {
    mode: 'light' | 'dark';
    variant: ThemeVariant;
    toggleTheme: () => void;
    setVariant: (variant: ThemeVariant) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [mode, setMode] = useState<'light' | 'dark'>(() => {
        const savedMode = localStorage.getItem('themeMode');
        if (savedMode === 'dark' || savedMode === 'light') return savedMode;
        return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    const [variant, setVariant] = useState<ThemeVariant>(() => {
        const savedVariant = localStorage.getItem('themeVariant') as ThemeVariant;
        return ['classic', 'pure-black', 'pure-white', 'trippy'].includes(savedVariant) ? savedVariant : 'classic';
    });

    useEffect(() => {
        localStorage.setItem('themeMode', mode);
        localStorage.setItem('themeVariant', variant);
        
        document.body.classList.remove('dark-mode', 'light-mode', 'theme-trippy', 'theme-black', 'theme-white');
        document.body.classList.add(`${mode}-mode`);
        document.body.setAttribute('data-theme', mode);
        document.body.setAttribute('data-variant', variant);
        
        if (variant === 'trippy') document.body.classList.add('theme-trippy');
        if (variant === 'pure-black') document.body.classList.add('theme-black');
        if (variant === 'pure-white') document.body.classList.add('theme-white');
    }, [mode, variant]);

    const toggleTheme = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));

    const theme = useMemo(() => {
        const isDark = mode === 'dark';
        const isBlack = variant === 'pure-black' && isDark;
        const isWhite = variant === 'pure-white' && !isDark;
        const isTrippy = variant === 'trippy';

        let primaryMain = '#FF6B9D';
        let secondaryMain = '#9B59B6';
        let bgDefault = isDark ? '#0A0A0A' : '#F5F8FA';
        let bgPaper = isDark ? '#141414' : '#FFFFFF';

        if (isBlack) {
            bgDefault = '#000000';
            bgPaper = '#080808';
        }

        if (isWhite) {
            bgDefault = '#FFFFFF';
            bgPaper = '#FAFAFA';
        }

        if (isTrippy) {
            primaryMain = '#00FFCC'; // Neon Cyan
            secondaryMain = '#FF00FF'; // Neon Magenta
            if (isDark) {
                bgDefault = '#0D0221'; // Deep space purple
                bgPaper = '#1A0836';
            }
        }

        return createTheme({
            palette: {
                mode,
                primary: {
                    main: primaryMain,
                    ...(isDark && {
                        light: isTrippy ? '#66FFDD' : '#FFB3C9',
                        dark: isTrippy ? '#00CCAA' : '#E05285',
                    }),
                },
                secondary: {
                    main: secondaryMain,
                    ...(isDark && {
                        light: isTrippy ? '#FF66FF' : '#C99FD6',
                        dark: isTrippy ? '#CC00CC' : '#7D3C98',
                    }),
                },
                background: {
                    default: bgDefault,
                    paper: bgPaper,
                },
                text: {
                    primary: isDark ? '#E0E0E0' : '#212121',
                    secondary: isDark ? '#B0B0B0' : '#757575',
                },
            },
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        body: {
                            backgroundColor: bgDefault,
                            color: isDark ? '#E0E0E0' : '#212121',
                            transition: 'background-color 0.3s ease, color 0.3s ease',
                        },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                            ...(isDark && {
                                backgroundColor: bgPaper,
                                border: `1px solid ${isBlack ? '#222' : '#333'}`,
                                borderRadius: 12,
                            }),
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                            borderRadius: 12,
                        },
                    },
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                            backgroundColor: bgPaper,
                            borderBottom: `1px solid ${isDark ? '#222' : '#eee'}`,
                            color: isDark ? '#E0E0E0' : '#212121',
                        },
                    },
                },
                MuiDrawer: {
                    styleOverrides: {
                        paper: {
                            backgroundImage: 'none',
                            backgroundColor: bgPaper,
                            borderRight: `1px solid ${isDark ? '#222' : '#eee'}`,
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            borderRadius: 8,
                            textTransform: 'none',
                            fontWeight: 600,
                        },
                    },
                },
            },
        });
    }, [mode, variant]);

    const value = useMemo(() => ({
        mode,
        variant,
        toggleTheme,
        setVariant,
    }), [mode, variant]);

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};
